import { FilterChip } from "@/components/FilterChip";
import { SPEND_RANGES, spendDayKeys, spendMonthKeys, spendRangeBounds, type SpendRange } from "@/lib/spend-range";
import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import { StatusBadge } from "@/components/StatusBadge";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { api } from "@/lib/api";
import { useSession } from "@/lib/auth-client";
import { formatCostLong, formatMoney, relativeIso } from "@/lib/format";
import { effectiveCap, fmtCap, fmtSpent } from "@/lib/spend-limits";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { TabStrip } from "@/components/TabStrip";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { allowedUsageTabs, USAGE_TABS, type UsageSearch, type UsageTab } from "@/lib/usage-tabs";
import { CreditBalanceTab } from "./billing/CreditBalanceTab";
import { InvoiceDetailsSection } from "./billing/InvoiceDetailsSection";
import { InvoicesSection } from "./billing/InvoicesSection";
import { SpendingLimitsSection } from "./billing/SpendingLimitsSection";

// The org's one home for usage and money: Usage | Spending limits | Invoices |
// Credit balance. Each tab is `?tab=`, so refresh and links keep it.

const TAB_LABEL: Record<UsageTab, string> = {
  usage: "Usage",
  limits: "Spending limits",
  invoices: "Invoices",
  balance: "Credit balance",
};

// One toast per return, even when the effect runs twice (React StrictMode).
const CHECKOUT_TOAST = "checkout-return";

export function UsagePage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { orgId: routeOrgId } = useParams({ strict: false }) as { orgId?: string };
  const search = useSearch({ strict: false }) as UsageSearch;

  // Unknown permissions (no active org, or GET /api/org failed) show every
  // tab, like the sidebar.
  const { data: session, isPending: sessionPending } = useSession();
  const orgId = session?.session.activeOrganizationId;
  const orgPerms = useQuery({
    queryKey: ["org-permissions", orgId],
    queryFn: api.org.permissions,
    enabled: !!orgId,
  });
  const perms = orgPerms.data?._permissions;
  const permsLoading = sessionPending || orgPerms.isLoading;
  const allowed = allowedUsageTabs(perms);
  const tabs = USAGE_TABS.filter((t) => allowed[t]);
  const active: UsageTab | undefined =
    search.tab && allowed[search.tab] ? search.tab : tabs[0];

  useDocumentTitle(active && active !== "usage" ? TAB_LABEL[active] : "Usage");

  // Hosted-checkout return handling. The mock provider processes the payment
  // before redirecting back, so an immediate refetch sees the new balance.
  // Real Stripe may deliver the webhook after the redirect — if balances look
  // stale after the swap, poll briefly here instead.
  useEffect(() => {
    if (!search.checkout && !search.setup) return;
    if (search.checkout === "success") {
      toast.success("Credits added to your balance", { id: CHECKOUT_TOAST });
      queryClient.invalidateQueries({ queryKey: ["billing-overview"] });
      queryClient.invalidateQueries({ queryKey: ["billing-transactions"] });
    } else if (search.setup === "success") {
      toast.success("Payment method saved", { id: CHECKOUT_TOAST });
      queryClient.invalidateQueries({ queryKey: ["billing-auto-recharge"] });
    } else {
      toast.info("Checkout cancelled", { id: CHECKOUT_TOAST });
    }
    navigate({ search: { tab: search.tab ?? "balance" } as never, replace: true });
  }, [search.checkout, search.setup, search.tab, queryClient, navigate]);

  const overview = useQuery({
    queryKey: ["billing-overview"],
    queryFn: api.billing.overview,
    enabled: !permsLoading && allowed.balance,
  });

  // Replace history so flipping tabs doesn't stack back-button entries.
  const setTab = (t: UsageTab) => {
    if (!routeOrgId) return;
    navigate({ to: "/orgs/$orgId/usage", params: { orgId: routeOrgId }, search: { tab: t }, replace: true });
  };

  if (permsLoading) return <LoadingIndicator />;
  if (!active) {
    return (
      <div className="p-6 space-y-6">
        <PageHeader title="Usage" />
        <p className="text-sm text-muted-foreground">You don't have permission to do this.</p>
      </div>
    );
  }

  const single = tabs.length === 1;
  const lowBalance = allowed.balance && overview.data !== undefined && overview.data.creditBalance < 2;

  return (
    <div className="p-6 space-y-6">
      {/* One allowed tab: no tab bar, and the title names what is left. */}
      <PageHeader
        title={single ? (active === "limits" ? "Your spending limits" : TAB_LABEL[active]) : "Usage"}
        subtitle={single ? undefined : "What your Boos cost, the limits on them, and how you pay."}
      />

      {lowBalance && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <AlertTriangle className="size-4 shrink-0" />
          Your credit balance is running low. Add credits to avoid service
          interruption.
        </div>
      )}

      {!single && (
        <TabStrip tabs={tabs.map((t) => ({ value: t, label: TAB_LABEL[t] }))} active={active} onSelect={setTab} label="Usage sections" />
      )}

      {active === "usage" && <UsageOverview />}
      {active === "limits" && <SpendingLimitsSection />}
      {active === "invoices" && (
        <div className="space-y-8">
          <InvoiceDetailsSection />
          <div className="border-t pt-8">
            <InvoicesSection />
          </div>
        </div>
      )}
      {active === "balance" && <CreditBalanceTab />}
    </div>
  );
}

// Cost view modeled on the reference console Cost page: filter chips, one
// total-spend number, and a stacked daily-spend chart (one series per Boo —
// spend is deliberately a single number, no cost-category breakdown).
// Everything derives from ONE dataset — ledger-backed day rows — so the
// card, the chart, and the Credit balance tab all tally.

const ORG_KEY = "orgAddons";
const SHARED_CHARGES = "Shared charges";

function UsageOverview() {
  const [range, setRange] = useState<SpendRange>("This month");
  const [booFilter, setBooFilter] = useState<string>("all");

  const allTime = range === "All time";
  const { start, end } = useMemo(
    () => (allTime ? { start: null, end: null } : spendRangeBounds(range)),
    [range, allTime],
  );
  const spend = useQuery({
    queryKey: ["org-spend-by-day", range],
    queryFn: () =>
      allTime
        ? api.usage.spendByMonthAllTime()
        : api.usage.spendByDay(start!.toISOString(), end!.toISOString()),
  });

  const data = spend.data;
  const containerName = useMemo(() => {
    const m = new Map<string, string>((data?.containers ?? []).map((c) => [c.id, c.name]));
    m.set(ORG_KEY, SHARED_CHARGES);
    return m;
  }, [data]);

  // Rows after the Boo filter — the shared basis for the card AND the
  // chart, so they can't disagree.
  const rows = useMemo(() => {
    const all = data?.days ?? [];
    if (booFilter === "all") return all;
    if (booFilter === ORG_KEY) return all.filter((r) => r.containerId === null);
    return all.filter((r) => r.containerId === booFilter);
  }, [data, booFilter]);

  const totalSpend = useMemo(() => rows.reduce((acc, r) => acc + r.total, 0), [rows]);

  // The org's monthly limit under this month's org-wide total. Viewers the
  // limits endpoint shows no org row (or refuses) get no subline.
  const monthOrgWide = range === "This month" && booFilter === "all";
  const limits = useQuery({
    queryKey: ["billing-spend-limits"],
    queryFn: api.billing.spendLimits.get,
    enabled: monthOrgWide,
    retry: false,
  });
  const orgMonthly = limits.data?.org?.monthly;
  const orgMonthlyCap = orgMonthly ? effectiveCap(orgMonthly) : null;
  // What is left is counted the way the limit counts it (its own month, in the
  // org's reset time zone), so it matches the Spending limits tab.
  const orgMonthlyLeft = orgMonthly && orgMonthlyCap != null ? Math.max(0, orgMonthlyCap - orgMonthly.spentUsd) : null;

  // One stacked bar per day (per month for All time), one series per Boo.
  const { chartData, seriesKeys, chartConfig } = useMemo(() => {
    const keys = allTime
      ? spendMonthKeys((data?.days ?? []).map((r) => r.day))
      : spendDayKeys(start!, end!);
    const series = new Set<string>();
    const byDay = new Map<string, Record<string, number | string>>(
      keys.map((k) => [k, { day: k }]),
    );
    for (const r of rows) {
      const key = r.containerId ?? ORG_KEY;
      series.add(key);
      const e = byDay.get(r.day);
      if (!e) continue;
      e[key] = ((e[key] as number | undefined) ?? 0) + r.total;
    }
    const seriesList = [...series];
    // Palette runs light→dark (--chart-1 is near-white); assign darkest
    // first so the dominant series is visible on white.
    const config: ChartConfig = Object.fromEntries(
      seriesList.map((k, i) => [
        k,
        { label: containerName.get(k) ?? k.slice(0, 8), color: `var(--chart-${5 - (i % 5)})` },
      ]),
    );
    return { chartData: [...byDay.values()], seriesKeys: seriesList, chartConfig: config };
  }, [rows, start, end, allTime, data, containerName]);

  if (spend.isLoading) return <LoadingIndicator />;
  if (spend.isError || !data) {
    return <ErrorComponent error={spend.error ?? "Failed to load usage"} />;
  }

  const booOptions = [
    { key: "all", label: "All" },
    ...data.containers.map((c) => ({ key: c.id, label: c.name })),
    ...(data.days.some((d) => d.containerId === null)
      ? [{ key: ORG_KEY, label: SHARED_CHARGES }]
      : []),
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip
          label="Boo"
          value={booFilter === "all" ? "All" : (containerName.get(booFilter) ?? "All")}
          options={booOptions}
          onSelect={setBooFilter}
        />
        <FilterChip
          label="Range"
          value={range}
          options={SPEND_RANGES.map((r) => ({ key: r, label: r }))}
          onSelect={(k) => setRange(k as SpendRange)}
        />
      </div>

      <div className="flex flex-col gap-1 rounded-lg border p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Total spend</div>
        <div className="text-3xl font-semibold tracking-tight">{formatCostLong(totalSpend)}</div>
        {monthOrgWide && orgMonthly && orgMonthlyCap != null && orgMonthlyLeft != null && (
          <div className="text-sm tabular-nums text-muted-foreground">
            Monthly limit: {fmtSpent(orgMonthly.spentUsd)} of {fmtCap(orgMonthlyCap)} · {fmtSpent(orgMonthlyLeft)} left
          </div>
        )}
        <div className="text-xs text-muted-foreground">
          Everything deducted from your credit balance in this range.
        </div>
      </div>

      <section className="rounded-lg border p-4">
        <h2 className="text-base font-semibold">{allTime ? "Monthly spend" : "Daily spend"}</h2>
        <ChartContainer config={chartConfig} className="mt-4 h-72 w-full">
          <BarChart data={chartData} margin={{ left: 8, right: 8 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="day"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={24}
              tickFormatter={(v: string) => {
                const [y, m, d] = v.split("-").map(Number);
                // 'YYYY-MM' keys (All time, monthly buckets) label as "Jul '26".
                if (d === undefined || Number.isNaN(d)) {
                  return new Date(y, m - 1, 1).toLocaleDateString(undefined, {
                    month: "short",
                    year: "numeric",
                  });
                }
                return new Date(y, m - 1, d).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                });
              }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={56}
              tickFormatter={(v: number) => formatMoney(v)}
            />
            <ChartTooltip content={<ChartTooltipContent valueFormatter={(v) => formatCostLong(Number(v))} />} />
            <ChartLegend content={<ChartLegendContent />} />
            {seriesKeys.map((k) => (
              // Fill straight from config — the ChartStyle --color-* vars
              // don't resolve for dynamic series keys in this setup.
              <Bar key={k} dataKey={k} stackId="spend" maxBarSize={96} fill={chartConfig[k]?.color} />
            ))}
          </BarChart>
        </ChartContainer>
      </section>

      <ActivitySection />
    </div>
  );
}

// Status + last activity per Boo, each row a door into that Boo's own Usage
// tab (where spend is broken down by person, channel and activity). No call
// counts: a request tally doesn't explain a bill and only invites questions.
function ActivitySection() {
  const { orgId } = useParams({ strict: false }) as { orgId?: string };
  const { data } = useQuery({ queryKey: ["org-usage"], queryFn: api.usage.org });
  if (!data || data.perContainer.length === 0) return null;
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold">Activity</h2>
      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Boo</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium text-right">Last activity</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.perContainer.map((c) => {
              const isDeleted = c.status === "deleted";
              return (
                <tr key={c.containerId} className={isDeleted ? "opacity-60" : undefined}>
                  <td className="px-3 py-2 font-medium">
                    {isDeleted || !orgId ? (
                      c.name
                    ) : (
                      <Link
                        to="/orgs/$orgId/containers/$containerId/usage"
                        params={{ orgId, containerId: c.containerId }}
                        className="underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-current"
                        title={`Usage for ${c.name}`}
                      >
                        {c.name}
                      </Link>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={isDeleted ? "deleted" : c.deploymentStatus} />
                  </td>
                  <td className="px-3 py-2 text-right text-muted-foreground">
                    {relativeIso(c.lastActivityAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
